<template>
  <div class="space-y-6 animate-fade-in pb-10">

    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Sucursales</h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm">Gestiona tus puntos de venta y centros de distribución.</p>
      </div>
      <button @click="openCreate" class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95">
        <Plus class="w-5 h-5" /> Nueva Sucursal
      </button>
    </div>

    <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
      <div class="relative flex-1 max-w-md">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input v-model="searchTerm" type="text" placeholder="Buscar por nombre o código..." class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all">
      </div>
    </div>

    <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
            <tr>
              <th class="px-6 py-4 font-semibold">Código / Nombre</th>
              <th class="px-6 py-4 font-semibold">Tipo</th>
              <th class="px-6 py-4 font-semibold">Contacto</th>
              <th class="px-6 py-4 font-semibold">Responsable</th>
              <th class="px-6 py-4 font-semibold">Estado</th>
              <th class="px-6 py-4 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
            <tr v-if="isLoading"><td colspan="6" class="px-6 py-8 text-center text-slate-500">Cargando...</td></tr>
            <tr v-else-if="filteredBranches.length === 0"><td colspan="6" class="px-6 py-8 text-center text-slate-500">Sin resultados.</td></tr>

            <tr v-else v-for="branch in filteredBranches" :key="branch.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">

              <td class="px-6 py-4">
                <div>
                  <div class="font-bold text-slate-900 dark:text-slate-100">{{ branch.name }}</div>
                  <div class="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded w-fit mt-1">
                    {{ branch.code }}
                  </div>
                </div>
              </td>

              <td class="px-6 py-4">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border bg-slate-50 border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300">
                  <component :is="getBranchIcon(branch.type)" class="w-3.5 h-3.5" />
                  {{ getBranchTypeLabel(branch.type) }}
                </span>
              </td>

              <td class="px-6 py-4 text-slate-500">
                <div class="flex items-center gap-2 text-xs mb-1" v-if="branch.phone">
                  <span class="opacity-70">Tel:</span> {{ branch.phone }}
                </div>
                <div class="flex items-start gap-1.5 text-xs max-w-[200px]" v-if="branch.address">
                  <MapPin class="w-3 h-3 flex-shrink-0 mt-0.5 opacity-50" />
                  <span class="truncate">{{ branch.address }}</span>
                </div>
              </td>

              <td class="px-6 py-4">
                <div v-if="branch.manager" class="text-xs font-medium text-white dark:text-white bg-brand-50 dark:bg-brand-900/20 px-2 py-1 rounded-md w-fit">
                  {{ managersMap[branch.manager] || 'ID: ' + branch.manager }}
                </div>
                <span v-else class="text-xs text-slate-400 italic">Sin asignar</span>
              </td>

              <td class="px-6 py-4">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
                  :class="Number(branch.is_active) === 1
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                    : 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'">
                  <span class="w-1.5 h-1.5 rounded-full" :class="Number(branch.is_active) === 1 ? 'bg-emerald-500' : 'bg-red-500'"></span>
                  {{ Number(branch.is_active) === 1 ? 'Operativa' : 'Cerrada' }}
                </span>
              </td>

              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <button @click="openEdit(branch)" class="p-2 text-slate-400 hover:text-white hover:bg-brand-50 rounded-lg transition-colors">
                    <Edit class="w-4 h-4" />
                  </button>
                  <button @click="deleteBranch(branch)" class="p-2 text-slate-400 hover:text-white hover:bg-red-900 rounded-lg transition-colors">
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </td>

            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <BranchForm
      :show="showModal"
      :branchToEdit="selectedBranch"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue';
import {
  Search, Plus, Edit, Trash2, MapPin, Store, Package, Building2
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import BranchForm from './Form.vue';
import { SecureStorage } from '@/utils/secureStorage';
import Swal from 'sweetalert2';

// Estado
const branches = ref([]);
const managersMap = ref({}); // Diccionario ID -> Nombre Gerente
const isLoading = ref(true);
const searchTerm = ref('');
const showModal = ref(false);
const selectedBranch = ref(null);

// Cargar Datos
const loadData = async () => {
  isLoading.value = true;
  try {
    const currentUser = SecureStorage.getItem('llevo_user');
    if (!currentUser?.company) throw new Error("Sin compañía.");
    const companyId = currentUser.company;

    // Carga paralela de Sucursales y Usuarios (para mapear gerentes)
    const [resBranches, resUsers] = await Promise.all([
      apiClient.get(`/branches?company=${companyId}`),
      apiClient.get(`/users?company=${companyId}`)
    ]);

    branches.value = Array.isArray(resBranches.data) ? resBranches.data : (resBranches.data.data || []);

    // Crear mapa de gerentes
    const users = Array.isArray(resUsers.data) ? resUsers.data : (resUsers.data.data || []);
    users.forEach(u => managersMap.value[u.id] = u.full_name);

  } catch (error) {
    console.error(error);
    Swal.fire({ toast: true, icon: 'error', title: 'Error al cargar datos' });
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

// Filtrado
const filteredBranches = computed(() => {
  if (!searchTerm.value) return branches.value;
  const term = searchTerm.value.toLowerCase();
  return branches.value.filter(b =>
    b.name.toLowerCase().includes(term) ||
    b.code.toLowerCase().includes(term)
  );
});

// Helpers UI
const getBranchIcon = (type) => {
  switch(type) {
    case 'WAREHOUSE': return Package;
    case 'HQ': return Building2;
    default: return Store;
  }
};

const getBranchTypeLabel = (type) => {
  switch(type) {
    case 'WAREHOUSE': return 'Bodega';
    case 'HQ': return 'Oficina Central';
    default: return 'Sucursal';
  }
};

// Acciones
const openCreate = () => { selectedBranch.value = null; showModal.value = true; };
const openEdit = (item) => { selectedBranch.value = item; showModal.value = true; };

const deleteBranch = (item) => {
  Swal.fire({
    title: '¿Desactivar sucursal?',
    text: `Se ocultará la sucursal ${item.name} del sistema operativo.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#f59e0b', // Naranja (warning)
    confirmButtonText: 'Sí, desactivar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        // En lugar de DELETE, hacemos un soft-delete (update is_active = 0)
        // o delete real si tu backend lo soporta y no hay ventas
        await apiClient.delete(`/branches/${item.id}`);
        branches.value = branches.value.filter(b => b.id !== item.id);
        Swal.fire('Procesado', 'Sucursal eliminada.', 'success');
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar.', 'error');
      }
    }
  });
};
</script>